<template>
    <div class="file-viewer-pdf tw:flex tw:flex-col tw:h-full tw:min-h-0" data-test="file-viewer-pdf">
        <file-viewer-controls class="tw:flex-wrap tw:gap-y-1" data-test="file-viewer-pdf-controls">
            <div class="tw:flex tw:items-center tw:gap-1" data-test="file-viewer-pdf-nav">
                <button
                    type="button"
                    class="button is-small is-white"
                    data-test="file-viewer-pdf-prev"
                    aria-label="Previous page"
                    :disabled="pageNumber <= 1"
                    @click="prevPage"
                >
                    Prev
                </button>
                <span class="tw:text-sm tw:tabular-nums" data-test="file-viewer-pdf-page" aria-live="polite">
                    Page {{ pageNumber }} of {{ numPages || '?' }}
                </span>
                <button
                    type="button"
                    class="button is-small is-white"
                    data-test="file-viewer-pdf-next"
                    aria-label="Next page"
                    :disabled="!numPages || pageNumber >= numPages"
                    @click="nextPage"
                >
                    Next
                </button>
            </div>
            <div class="tw:flex tw:items-center tw:gap-1 tw:ml-auto" data-test="file-viewer-pdf-zoom-controls">
                <button
                    type="button"
                    class="button is-small is-white"
                    data-test="file-viewer-pdf-zoom-out"
                    aria-label="Zoom out"
                    :disabled="scale <= minScale"
                    @click="zoomOut"
                >
                    <i class="uil uil-minus" aria-hidden="true" />
                </button>
                <span class="tw:text-sm tw:tabular-nums" data-test="file-viewer-pdf-zoom" aria-live="polite">
                    {{ Math.round(scale * 100) }}%
                </span>
                <button
                    type="button"
                    class="button is-small is-white"
                    data-test="file-viewer-pdf-zoom-in"
                    aria-label="Zoom in"
                    :disabled="scale >= maxScale"
                    @click="zoomIn"
                >
                    <i class="uil uil-plus" aria-hidden="true" />
                </button>
                <button
                    type="button"
                    class="button is-small is-white"
                    data-test="file-viewer-pdf-fit-width"
                    aria-label="Fit to width"
                    @click="fitToWidth"
                >
                    <i class="uil uil-expand-arrows-alt" aria-hidden="true" />
                </button>
            </div>
        </file-viewer-controls>

        <div
            ref="container"
            class="tw:flex-1 tw:min-h-0 tw:overflow-auto tw:p-4 tw:bg-gray-50 tw:[scrollbar-gutter:stable]"
            data-test="file-viewer-pdf-viewport"
        >
            <canvas ref="canvas" class="tw:block tw:bg-white tw:shadow-sm" data-test="file-viewer-pdf-canvas" />
        </div>
    </div>
</template>

<script setup>
// eslint-disable-next-line import/extensions -- Vite ?url asset import for pdf.js worker
import pdfWorkerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import FileViewerControls from '@/components/file-viewer/FileViewerControls.vue';

const props = defineProps({
    src: {
        type: String,
        required: true,
    },
});

const emit = defineEmits(['ready', 'error']);

const canvas = ref(null);
const container = ref(null);
let pdfDoc = null;
let loadingTask = null;
let renderTask = null;
let loadId = 0;
/** Bumps on every render/fit request so slower earlier pages cannot paint over newer ones. */
let renderId = 0;
const numPages = ref(0);
const pageNumber = ref(1);
const minScale = 0.5;
const maxScale = 3;
const scaleStep = 0.25;
const defaultMaxScale = 1.25;
/** Horizontal padding of the viewport: `tw:p-4` on the left and the right. */
const viewportPadding = 32;
const scale = ref(defaultMaxScale);
/** Set once the user zooms or fits; stops the default zoom from following viewport resizes. */
let userZoomed = false;
let lastViewportWidth = 0;
/** @type {ResizeObserver | null} */
let resizeObserver = null;

/**
 * @param {number} value
 * @returns {number}
 */
function clampScale(value) {
    return Math.max(minScale, Math.min(maxScale, value));
}

/**
 * Zoom that makes the page fill the viewport's inner width, rounded down to 2 decimals
 * so rounding never pushes the page past the edge. The viewport reserves its scrollbar
 * gutter, so `clientWidth` already excludes the vertical scrollbar.
 *
 * @param {number} pageWidth width of the page at 100%
 * @returns {number | null} null while the viewport has no measurable width
 */
function fitWidthScale(pageWidth) {
    const viewportWidth = container.value?.clientWidth ?? 0;
    if (viewportWidth <= 0 || pageWidth <= 0) {
        return null;
    }

    const availableWidth = Math.max(viewportWidth - viewportPadding, 1);

    return Math.floor((availableWidth / pageWidth) * 100 + 1e-9) / 100;
}

/**
 * Default zoom: 125%, or less when that would make the page wider than the viewport.
 *
 * @param {import('pdfjs-dist').PDFPageProxy} page
 * @returns {number}
 */
function defaultScaleFor(page) {
    const fitScale = fitWidthScale(page.getViewport({ scale: 1 }).width);
    if (fitScale === null) {
        return defaultMaxScale;
    }

    return clampScale(Math.min(defaultMaxScale, fitScale));
}

/**
 * @param {number} num
 */
async function renderPage(num) {
    if (!pdfDoc) {
        return;
    }

    const id = ++renderId;
    renderTask?.cancel();
    renderTask = null;

    const page = await pdfDoc.getPage(num);
    if (id !== renderId) {
        return;
    }

    const viewport = page.getViewport({ scale: scale.value });
    const c = canvas.value;
    if (!c || id !== renderId) {
        return;
    }

    c.width = Math.floor(viewport.width);
    c.height = Math.floor(viewport.height);
    const ctx = c.getContext('2d');
    renderTask = page.render({ canvasContext: ctx, canvas: c, viewport });
    try {
        await renderTask.promise;
    } catch (e) {
        if (id !== renderId || e?.name === 'RenderingCancelledException') {
            return;
        }
        emit('error', e);
    }
}

/**
 * @param {string} url
 */
async function loadPdf(url) {
    const id = ++loadId;
    try {
        // eslint-disable-next-line import/extensions -- pdfjs package subpath
        const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
        if (id !== loadId) {
            return;
        }
        pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

        // Fetch on the main thread (same auth path as DOCX/XLSX) so session cookies
        // and relative /media URLs work; pdf.js v6 also requires { url|data }, not a bare string.
        const resp = await fetch(url, { credentials: 'same-origin' });
        if (id !== loadId) {
            return;
        }
        if (!resp.ok) {
            throw new Error(`Failed to fetch PDF (${resp.status})`);
        }

        const arrayBuf = await resp.arrayBuffer();
        if (id !== loadId) {
            return;
        }
        const data = new Uint8Array(arrayBuf);
        const task = pdfjs.getDocument({ data });
        loadingTask = task;
        const doc = await task.promise;
        if (id !== loadId) {
            // If this load is stale, destroy the doc we just loaded and ignore the result.
            try {
                doc.destroy();
            } catch (e) {
                // ignore
            }
            return;
        }
        pdfDoc = doc;
        numPages.value = pdfDoc.numPages;
        pageNumber.value = Math.min(pageNumber.value, pdfDoc.numPages);
        const firstPage = await pdfDoc.getPage(pageNumber.value);
        if (id !== loadId) {
            return;
        }
        if (!userZoomed) {
            scale.value = defaultScaleFor(firstPage);
            lastViewportWidth = container.value?.clientWidth ?? 0;
        }
        await renderPage(pageNumber.value);
        if (id !== loadId) {
            return;
        }
        emit('ready');
    } catch (err) {
        // Ignore errors from stale loads so an old request cannot overwrite the current state
        if (id !== loadId) {
            return;
        }
        emit('error', err);
    }
}

function teardown() {
    renderId += 1;
    renderTask?.cancel();
    renderTask = null;
    // Some pdfjs versions expose destroy(), others provide cancel() on the loadingTask.
    if (loadingTask) {
        if (typeof loadingTask.destroy === 'function') {
            loadingTask.destroy();
        } else if (typeof loadingTask.cancel === 'function') {
            // cancel() is a safe fallback when destroy() is unavailable.
            loadingTask.cancel();
        }
    }
    loadingTask = null;
    pdfDoc = null;
}

function nextPage() {
    if (pageNumber.value < numPages.value) {
        pageNumber.value += 1;
        renderPage(pageNumber.value);
    }
}

function prevPage() {
    if (pageNumber.value > 1) {
        pageNumber.value -= 1;
        renderPage(pageNumber.value);
    }
}

function zoomIn() {
    userZoomed = true;
    scale.value = Math.min(maxScale, Number((scale.value + scaleStep).toFixed(2)));
    renderPage(pageNumber.value);
}

function zoomOut() {
    userZoomed = true;
    scale.value = Math.max(minScale, Number((scale.value - scaleStep).toFixed(2)));
    renderPage(pageNumber.value);
}

async function fitToWidth() {
    if (!pdfDoc || !container.value) {
        return;
    }

    userZoomed = true;
    const id = ++renderId;
    renderTask?.cancel();
    renderTask = null;

    try {
        const page = await pdfDoc.getPage(pageNumber.value);
        if (id !== renderId) {
            return;
        }

        const fitScale = fitWidthScale(page.getViewport({ scale: 1 }).width);
        if (fitScale === null) {
            return;
        }
        scale.value = clampScale(fitScale);
        await renderPage(pageNumber.value);
    } catch (e) {
        if (id !== renderId) {
            return;
        }
        emit('error', e);
    }
}

/**
 * Re-applies the default zoom when the viewport's width changes (maximize, restore,
 * window resize, drawer animation) until the user picks a zoom of their own.
 */
async function refitToDefault() {
    const viewportWidth = container.value?.clientWidth ?? 0;
    if (!pdfDoc || userZoomed || viewportWidth <= 0 || viewportWidth === lastViewportWidth) {
        return;
    }
    lastViewportWidth = viewportWidth;

    // Guard on the document, not renderId: a page change while this awaits must not drop
    // the re-fit, because no further resize event may come once the drawer settles.
    const doc = pdfDoc;
    try {
        const page = await doc.getPage(pageNumber.value);
        if (doc !== pdfDoc || userZoomed) {
            return;
        }

        const nextScale = defaultScaleFor(page);
        if (nextScale === scale.value) {
            return;
        }
        scale.value = nextScale;
        await renderPage(pageNumber.value);
    } catch (e) {
        if (doc !== pdfDoc) {
            return;
        }
        emit('error', e);
    }
}

onMounted(() => {
    if (typeof ResizeObserver === 'undefined' || !container.value) {
        return;
    }
    resizeObserver = new ResizeObserver(() => {
        refitToDefault();
    });
    resizeObserver.observe(container.value);
});

watch(
    () => props.src,
    value => {
        pageNumber.value = 1;
        numPages.value = 0;
        scale.value = defaultMaxScale;
        userZoomed = false;
        lastViewportWidth = 0;
        teardown();
        if (value) {
            loadPdf(value);
        }
    },
    { immediate: true }
);

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    resizeObserver = null;
    loadId += 1;
    teardown();
});
</script>
